import { DSR_TYPES, type DsrType, NOTICE_KINDS, type NoticeKind } from '@ambernord/shared';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonRows,
  StatusBadge,
  THead,
  TableShell,
  Td,
  Th,
  Tr,
  useToast,
} from '@ambernord/ui';
import { FileWarning, Mail, ShieldQuestion } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppLayout, DataState } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';

interface DsrItem {
  id: string;
  type: string;
  status: string;
  receivedAt: string | null;
  forwardDeadline: string | null;
  assistDeadline: string | null;
}

interface BreachItem {
  id: string;
  detectedAt: string | null;
  notifyDeadline: string | null;
  status: string;
  scope: string | null;
}

interface NoticeItem {
  id: string;
  kind: string;
  subject: string;
  sentAt: string | null;
  ackAt: string | null;
  trackingRef: string | null;
}

function DsrSection() {
  const { t, i18n } = useTranslation('compliance');
  const { toast } = useToast();
  const [items, setItems] = useState<DsrItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<DsrType>(DSR_TYPES[0]);
  const [subjectRef, setSubjectRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const statusLabel = (code: string) => t(`statuses:${code.toLowerCase()}`, { defaultValue: code });

  const load = useCallback(async () => {
    const res = await api.get<{ items: DsrItem[] }>('/api/compliance/dsr');
    if (!res.ok) {
      setError(t('dsr.errors.load', { status: res.status }));
      setItems([]);
      return;
    }
    setError(null);
    setItems(res.data.items ?? []);
  }, [t]);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: DsrItem[] }>('/api/compliance/dsr');
      if (!active) return;
      if (!res.ok) {
        setError(t('dsr.errors.load', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, [t]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const trimmed = subjectRef.trim();
      const res = await api.post<{ error?: string }>('/api/compliance/dsr', {
        type,
        ...(trimmed ? { subjectRef: trimmed } : {}),
      });
      if (!res.ok) {
        const msg = res.data.error ?? t('dsr.errors.create', { status: res.status });
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setSubjectRef('');
      toast(t('toast.dsrCreated'));
      await load();
    } catch {
      const msg = t('dsr.errors.createGeneric');
      setFormError(msg);
      toast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('dsr.heading')} subtitle={t('dsr.explainer')} />
      <CardBody className="flex flex-col gap-4">
        <form
          className="flex flex-col gap-4 sm:flex-row sm:items-end"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Field label={t('dsr.form.type')} htmlFor="dsr-type">
            <Select id="dsr-type" value={type} onChange={(e) => setType(e.target.value as DsrType)}>
              {DSR_TYPES.map((d) => (
                <option key={d} value={d}>
                  {t(`dsr.type.${d}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('dsr.form.subjectRef')} htmlFor="dsr-subject" className="flex-1">
            <Input
              id="dsr-subject"
              type="text"
              value={subjectRef}
              onChange={(e) => setSubjectRef(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('actions.saving') : t('dsr.actions.create')}
          </Button>
        </form>
        {formError && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        )}
        {error ? (
          <DataState loading={false} error={error} empty={false}>
            {null}
          </DataState>
        ) : items === null ? (
          <SkeletonRows rows={3} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<ShieldQuestion className="h-6 w-6" />}
            title={t('dsr.empty.title')}
            hint={t('dsr.empty.hint')}
          />
        ) : (
          <TableShell>
            <THead>
              <tr>
                <Th>{t('dsr.table.type')}</Th>
                <Th>{t('dsr.table.status')}</Th>
                <Th>{t('dsr.table.received')}</Th>
                <Th>{t('dsr.table.forwardDeadline')}</Th>
                <Th>{t('dsr.table.assistDeadline')}</Th>
              </tr>
            </THead>
            <tbody>
              {items.map((d) => (
                <Tr key={d.id}>
                  <Td className="font-medium text-slate-900 dark:text-white">
                    {t(`dsr.type.${d.type}`, { defaultValue: d.type })}
                  </Td>
                  <Td>
                    <StatusBadge status={d.status} label={statusLabel(d.status)} />
                  </Td>
                  <Td>{formatDateTime(d.receivedAt, i18n.language)}</Td>
                  <Td>{formatDateTime(d.forwardDeadline, i18n.language)}</Td>
                  <Td>{formatDateTime(d.assistDeadline, i18n.language)}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </CardBody>
    </Card>
  );
}

function BreachSection() {
  const { t, i18n } = useTranslation('compliance');
  const { toast } = useToast();
  const [items, setItems] = useState<BreachItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const statusLabel = (code: string) => t(`statuses:${code.toLowerCase()}`, { defaultValue: code });

  const load = useCallback(async () => {
    const res = await api.get<{ items: BreachItem[] }>('/api/compliance/breaches');
    if (!res.ok) {
      setError(t('breaches.errors.load', { status: res.status }));
      setItems([]);
      return;
    }
    setError(null);
    setItems(res.data.items ?? []);
  }, [t]);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: BreachItem[] }>('/api/compliance/breaches');
      if (!active) return;
      if (!res.ok) {
        setError(t('breaches.errors.load', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, [t]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const trimmed = scope.trim();
    if (!trimmed) {
      setFormError(t('breaches.errors.scopeRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post<{ error?: string }>('/api/compliance/breaches', {
        scope: trimmed,
      });
      if (!res.ok) {
        const msg = res.data.error ?? t('breaches.errors.record', { status: res.status });
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setScope('');
      toast(t('toast.breachRecorded'));
      await load();
    } catch {
      const msg = t('breaches.errors.recordGeneric');
      setFormError(msg);
      toast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('breaches.heading')} subtitle={t('breaches.explainer')} />
      <CardBody className="flex flex-col gap-4">
        <form
          className="flex flex-col gap-4 sm:flex-row sm:items-end"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Field label={t('breaches.form.scope')} htmlFor="breach-scope" className="flex-1">
            <Input
              id="breach-scope"
              type="text"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('actions.saving') : t('breaches.actions.record')}
          </Button>
        </form>
        {formError && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        )}
        {error ? (
          <DataState loading={false} error={error} empty={false}>
            {null}
          </DataState>
        ) : items === null ? (
          <SkeletonRows rows={3} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<FileWarning className="h-6 w-6" />}
            title={t('breaches.empty.title')}
            hint={t('breaches.empty.hint')}
          />
        ) : (
          <TableShell>
            <THead>
              <tr>
                <Th>{t('breaches.table.detected')}</Th>
                <Th>{t('breaches.table.notifyDeadline')}</Th>
                <Th>{t('breaches.table.status')}</Th>
                <Th>{t('breaches.table.scope')}</Th>
              </tr>
            </THead>
            <tbody>
              {items.map((b) => (
                <Tr key={b.id}>
                  <Td>{formatDateTime(b.detectedAt, i18n.language)}</Td>
                  <Td>{formatDateTime(b.notifyDeadline, i18n.language)}</Td>
                  <Td>
                    <StatusBadge status={b.status} label={statusLabel(b.status)} />
                  </Td>
                  <Td>{b.scope ?? '—'}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </CardBody>
    </Card>
  );
}

function NoticeSection() {
  const { t, i18n } = useTranslation('compliance');
  const { toast } = useToast();
  const [items, setItems] = useState<NoticeItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<NoticeKind>(NOTICE_KINDS[0]);
  const [subject, setSubject] = useState('');
  const [trackingRef, setTrackingRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<{ items: NoticeItem[] }>('/api/compliance/notices');
    if (!res.ok) {
      setError(t('notices.errors.load', { status: res.status }));
      setItems([]);
      return;
    }
    setError(null);
    setItems(res.data.items ?? []);
  }, [t]);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await api.get<{ items: NoticeItem[] }>('/api/compliance/notices');
      if (!active) return;
      if (!res.ok) {
        setError(t('notices.errors.load', { status: res.status }));
        setItems([]);
        return;
      }
      setItems(res.data.items ?? []);
    })();
    return () => {
      active = false;
    };
  }, [t]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    const trimmedSubject = subject.trim();
    if (!trimmedSubject) {
      setFormError(t('notices.errors.subjectRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const trimmedRef = trackingRef.trim();
      const res = await api.post<{ error?: string }>('/api/compliance/notices', {
        kind,
        subject: trimmedSubject,
        ...(trimmedRef ? { trackingRef: trimmedRef } : {}),
      });
      if (!res.ok) {
        const msg = res.data.error ?? t('notices.errors.create', { status: res.status });
        setFormError(msg);
        toast(msg, 'error');
        return;
      }
      setSubject('');
      setTrackingRef('');
      toast(t('toast.noticeCreated'));
      await load();
    } catch {
      const msg = t('notices.errors.createGeneric');
      setFormError(msg);
      toast(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t('notices.heading')} />
      <CardBody className="flex flex-col gap-4">
        <form
          className="flex flex-col gap-4 sm:flex-row sm:items-end"
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Field label={t('notices.form.kind')} htmlFor="notice-kind">
            <Select
              id="notice-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as NoticeKind)}
            >
              {NOTICE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`notices.kind.${k}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('notices.form.subject')} htmlFor="notice-subject" className="flex-1">
            <Input
              id="notice-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </Field>
          <Field label={t('notices.form.trackingRef')} htmlFor="notice-ref" className="flex-1">
            <Input
              id="notice-ref"
              type="text"
              value={trackingRef}
              onChange={(e) => setTrackingRef(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('actions.saving') : t('notices.actions.create')}
          </Button>
        </form>
        {formError && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {formError}
          </p>
        )}
        {error ? (
          <DataState loading={false} error={error} empty={false}>
            {null}
          </DataState>
        ) : items === null ? (
          <SkeletonRows rows={3} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Mail className="h-6 w-6" />}
            title={t('notices.empty.title')}
            hint={t('notices.empty.hint')}
          />
        ) : (
          <TableShell>
            <THead>
              <tr>
                <Th>{t('notices.table.kind')}</Th>
                <Th>{t('notices.table.subject')}</Th>
                <Th>{t('notices.table.sent')}</Th>
                <Th>{t('notices.table.acknowledged')}</Th>
                <Th>{t('notices.table.trackingRef')}</Th>
              </tr>
            </THead>
            <tbody>
              {items.map((n) => (
                <Tr key={n.id}>
                  <Td className="font-medium text-slate-900 dark:text-white">
                    {t(`notices.kind.${n.kind}`, { defaultValue: n.kind })}
                  </Td>
                  <Td>{n.subject}</Td>
                  <Td>{formatDateTime(n.sentAt, i18n.language)}</Td>
                  <Td>{formatDateTime(n.ackAt, i18n.language)}</Td>
                  <Td>{n.trackingRef ?? '—'}</Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </CardBody>
    </Card>
  );
}

export default function Compliance() {
  const { t } = useTranslation('compliance');
  return (
    <AppLayout title={t('title')}>
      <div className="flex flex-col gap-6">
        <DsrSection />
        <BreachSection />
        <NoticeSection />
      </div>
    </AppLayout>
  );
}
